<template>
    <div class="space-y-2">
        <div class="flex items-center justify-between">
            <div class="text-2xl font-medium text-gray-900">
                Customer Information
            </div>

            <prime-button
                v-if="!editing"
                text
                icon="pi pi-pencil"
                label="Edit"
                :disabled="isProcessing"
                @click="$emit('update:editing', true)"
            />
        </div>

        <div
            v-if="!editing"
            class="grid grid-cols-2 gap-x-6 gap-y-2 text-lg md:gap-x-12"
        >
            <div>
                <div class="text-sm font-medium text-gray-500">Name</div>
                <div class="text-gray-900">
                    {{ firstName }}
                    {{ middleName }}
                    {{ lastName }}
                </div>
            </div>

            <div>
                <div class="text-sm font-medium text-gray-500">
                    Date of Birth
                </div>
                <div v-if="dateOfBirth">
                    {{ formatTimelessDate(parseTimeless(dateOfBirth)) }}
                </div>
                <div
                    v-else
                    class="text-gray-300"
                >
                    &mdash;
                </div>
            </div>

            <div>
                <div class="text-sm font-medium text-gray-500">
                    Mobile Phone
                </div>
                <div v-if="mobilePhoneInitial">
                    {{ mobilePhoneInitial }}
                </div>
                <div
                    v-else
                    class="text-gray-300"
                >
                    &mdash;
                </div>
            </div>

            <div>
                <div class="text-sm font-medium text-gray-500">
                    Email Address
                </div>
                <div>
                    {{ email }}
                </div>
            </div>
        </div>

        <div
            v-if="editing"
            class="grid grid-cols-12 gap-x-4 gap-y-1"
        >
            <!-- Name -->

            <div class="col-span-12 lg:col-span-4">
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span>First Name
                </div>
                <input-text
                    id="given_name"
                    v-model="firstName"
                    autocomplete="given-name"
                    placeholder="First Name"
                    class="w-full"
                    :disabled="isProcessing"
                    :pt="{
                        root: {
                            class:
                                showErrors && errorFirstName
                                    ? 'border-red-600'
                                    : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
                <div
                    v-if="showErrors"
                    class="text-red-600 text-sm"
                >
                    {{ errorFirstName || '&nbsp;' }}
                </div>
            </div>
            <div class="col-span-12 lg:col-span-4">
                <div class="text-sm text-gray-500">Middle Name</div>
                <input-text
                    id="middle_name"
                    v-model="middleName"
                    autocomplete="additional-name"
                    placeholder="Middle Name"
                    class="w-full"
                    :disabled="isProcessing"
                />
                <div class="text-red-600 text-sm">
                    {{ errorMiddleName || '&nbsp;' }}
                </div>
            </div>
            <div class="col-span-12 lg:col-span-4">
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span>Last Name
                </div>
                <input-text
                    id="last_name"
                    v-model="lastName"
                    autocomplete="last-name"
                    placeholder="Last Name"
                    class="w-full"
                    :disabled="isProcessing"
                    :pt="{
                        root: {
                            class:
                                showErrors && errorLastName
                                    ? 'border-red-600'
                                    : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
                <div
                    v-if="showErrors"
                    class="text-red-600 text-sm"
                >
                    {{ errorLastName || '&nbsp;' }}
                </div>
            </div>

            <!-- Date of Birth -->

            <div class="col-span-12 lg:col-span-5">
                <div class="text-sm text-gray-500">Date of Birth</div>
                <dropdown-date-input
                    v-model:date="dateOfBirth"
                    :show-errors="false"
                    :disabled="isProcessing"
                />
            </div>

            <!-- Email -->

            <div class="col-span-12">
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span>Email Address
                </div>
                <input-text
                    id="email"
                    v-model="email"
                    inputmode="email"
                    type="email"
                    autocomplete="email"
                    placeholder="Email Address"
                    class="w-full"
                    :disabled="isProcessing"
                    :pt="{
                        root: {
                            class:
                                showErrors && errorEmail ? 'border-red-600' : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
                <div
                    v-if="showErrors && errorEmail"
                    class="text-red-600 text-sm"
                >
                    {{ errorEmail }}
                </div>
            </div>

            <!-- Do Not Mail -->

            <div class="col-span-12">
                <div class="ml-4 flex gap-2 pt-1">
                    <prime-checkbox
                        v-model="doNotMailComputed"
                        input-id="mailing_list_checkbox"
                        :binary="true"
                        :disabled="isProcessing"
                    />
                    <label for="mailing_list_checkbox">
                        {{ facilityData.doNotMailPromptText }}
                    </label>
                </div>
                <div class="text-sm">&nbsp;</div>
            </div>

            <!-- Mobile Phone -->

            <div class="col-span-12">
                <div class="text-sm text-gray-500">Mobile Phone</div>
                <phone-number
                    v-model="mobilePhone"
                    :initial-value="mobilePhoneInitial"
                    placeholder="Mobile Phone"
                    :autofocus="false"
                    :disabled="isProcessing"
                />
                <div class="text-red-600 text-sm">
                    {{ errorMobilePhone || '&nbsp;' }}
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData, graphql } from '~/resources/graphql'
import { boolean, object, string } from 'yup'
import {
    doNotMailChecked,
    doNotMailDefault,
    doNotMailValue
} from '~/components/PurchaseContract/do-not-mail-helpers'
import { toTypedSchema } from '@vee-validate/yup'
import type { TypedSchema } from 'vee-validate'

export const CustomerInformationCustomerFragment = graphql(/* GraphQL */ `
    fragment CustomerInformationCustomerFragment on Customer {
        id
        firstName
        middleName
        lastName
        dateOfBirth
        email
        doNotMail
        mobilePhone
    }
`)

export const CustomerInformationFacilityFragment = graphql(/* GraphQL */ `
    fragment CustomerInformationFacilityFragment on Facility {
        doNotMailDirection
        doNotMailDefaultValue
        doNotMailPromptText(language: ENGLISH)
    }
`)

export const CustomerInformationCheckoutFormSchema = object({
    enrollmentId: string(),
    firstName: string()
        .min(1, 'First name required')
        .max(255)
        .required()
        .label('First name'),
    middleName: string().max(255).label('Middle name'),
    lastName: string()
        .min(2, '2 characters required')
        .max(255)
        .required()
        .label('Last name'),
    dateOfBirth: string().label('Date of birth'),
    mobilePhone: object()
        .shape({
            formatted: string(),
            number: string(),
            valid: boolean()
        })
        .test({
            message: 'Please enter a valid phone number',
            test: value =>
                value.number === undefined ||
                value.number === '' ||
                value.valid === true
        }),
    email: string()
        .required()
        .label('Customer email')
        .email('Please enter a valid email'),
    doNotMail: boolean().nullable().label('Do not mail')
})

export function CustomerInformationCheckoutFormInitialValues(
    customer: FragmentType<typeof CustomerInformationCustomerFragment> | null,
    facility: FragmentType<typeof CustomerInformationFacilityFragment>,
    onlineGuestId: string
) {
    let customerData =
        customer === null
            ? null
            : getFragmentData(CustomerInformationCustomerFragment, customer)

    if (customerData?.id === onlineGuestId) {
        customerData = null
    }

    const facilityData = getFragmentData(
        CustomerInformationFacilityFragment,
        facility
    )

    return {
        firstName: customerData?.firstName ?? '',
        middleName: customerData?.middleName ?? '',
        lastName: customerData?.lastName ?? '',
        dateOfBirth: customerData?.dateOfBirth ?? '',
        email: customerData?.email ?? '',
        doNotMail: doNotMailDefault(
            facilityData.doNotMailDirection,
            facilityData.doNotMailDefaultValue
        )
    }
}

export const customerInformationSchema = toTypedSchema(
    CustomerInformationCheckoutFormSchema
)
export type CustomerInformationSchemaType =
    typeof customerInformationSchema extends TypedSchema<unknown, infer TOutput>
        ? TOutput
        : never
</script>

<script setup lang="ts">
import InputText from 'primevue/inputtext'
import PrimeButton from 'primevue/button'
import PhoneNumber from '~/components/Input/PhoneNumber.vue'
import { useField } from 'vee-validate'
import {
    formatTimelessDate,
    parseTimeless
} from '~/composables/support/localization'
import { computed, nextTick, onMounted, watch } from 'vue'
import PrimeCheckbox from 'primevue/checkbox'
import DropdownDateInput from '~/components/Input/DropdownDateInput.vue'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'

const props = defineProps<{
    customer: FragmentType<typeof CustomerInformationCustomerFragment>
    facility: FragmentType<typeof CustomerInformationFacilityFragment>
    showErrors: boolean
    editing: boolean
}>()

const emit = defineEmits<{
    (e: 'update:editing', payload: boolean): void
}>()

const customerData = computed(() =>
    getFragmentData(CustomerInformationCustomerFragment, props.customer)
)

const facilityData = computed(() =>
    getFragmentData(CustomerInformationFacilityFragment, props.facility)
)

onMounted(() => {
    if (customerData.value.email === '') {
        nextTick(() => document.getElementById('email')?.focus())
    }
})

const { value: firstName, errorMessage: errorFirstName } =
    useField('customer.firstName')
const { value: middleName, errorMessage: errorMiddleName } = useField(
    'customer.middleName'
)
const { value: lastName, errorMessage: errorLastName } =
    useField('customer.lastName')
const { value: dateOfBirth, errorMessage: errorDateOfBirth } = useField(
    'customer.dateOfBirth'
)
const { value: email, errorMessage: errorEmail } = useField('customer.email')
const { value: mobilePhone, errorMessage: errorMobilePhone } = useField(
    'customer.mobilePhone'
)
const { value: doNotMail } = useField<boolean | null>('customer.doNotMail')

function shouldShowDefaultDoNotMailChange(): boolean {
    const defaultDoNotMail = doNotMailDefault(
        facilityData.value.doNotMailDirection,
        facilityData.value.doNotMailDefaultValue
    )

    return (
        defaultDoNotMail !== null &&
        customerData.value.doNotMail !== defaultDoNotMail
    )
}
emit(
    'update:editing',
    customerData.value.email === '' || shouldShowDefaultDoNotMailChange()
)

const doNotMailComputed = computed({
    get() {
        return doNotMailChecked(
            facilityData.value.doNotMailDirection,
            doNotMail.value
        )
    },
    set(newValue) {
        doNotMail.value = doNotMailValue(
            facilityData.value.doNotMailDirection,
            newValue
        )
    }
})

const mobilePhoneInitial = computed<string>(() => {
    if (
        typeof mobilePhone.value.number === 'string' &&
        mobilePhone.value.number !== ''
    ) {
        return mobilePhone.value.number
    }

    return customerData.value.mobilePhone
})

watch(
    [
        errorFirstName,
        errorMiddleName,
        errorLastName,
        errorDateOfBirth,
        errorEmail,
        errorMobilePhone
    ],
    ([
        newValueFirstName,
        newValueMiddleName,
        newValueLastName,
        newValueDateOfBirth,
        newValueEmail,
        newValueMobilePhone
    ]) => {
        if (
            newValueFirstName ||
            newValueMiddleName ||
            newValueLastName ||
            newValueDateOfBirth ||
            newValueEmail ||
            newValueMobilePhone
        ) {
            emit('update:editing', true)
        }
    }
)

const { isProcessing } = useCheckoutFormProcessing()
</script>
